


import React, { useState, useEffect } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Building2, Mail, Phone, MapPin, Globe, Upload, 
  Save, Loader2, Edit3, X, CheckCircle, Camera
} from "lucide-react";

const CompanyProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    officialEmail: "",
    phoneNumber: "",
    cityAndCountry: "",
    otherOfficeLocations: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.COMPANY_PROFILE_BASE}`);
      if (res.ok) {
        const data = await res.json();
        setFormData({
          companyName: data.companyName || "",
          officialEmail: data.officialEmail || "",
          phoneNumber: data.phoneNumber || "",
          cityAndCountry: data.cityAndCountry || "",
          otherOfficeLocations: data.otherOfficeLocations || "",
        });
        if (data.logoPath) {
          setLogoPreview(`${APIConfig.APIACTIVEURL.replace('/api/', '/')}${data.logoPath}`);
        }
      }
    } catch (err) { console.error(err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const data = new FormData();
    data.append("CompanyName", formData.companyName);
    data.append("OfficialEmail", formData.officialEmail);
    data.append("PhoneNumber", formData.phoneNumber);
    data.append("CityAndCountry", formData.cityAndCountry);
    data.append("OtherOfficeLocations", formData.otherOfficeLocations);
    if (logoFile) data.append("LogoFile", logoFile);

    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.COMPANY_PROFILE_UPSERT}`, {
        method: "POST",
        body: data,
      });
      if (res.ok) {
        Swal.fire({ icon: 'success', title: 'Profile Updated', showConfirmButton: false, timer: 1500 });
        setIsEditing(false);
        fetchProfile();
      }
    } catch (err) { Swal.fire("Error", "Could not save", "error"); }
    finally { setLoading(false); }
  };

  // --- UI COMPONENTS ---

  // Card for View Mode
  const InfoCard = ({ icon: Icon, label, value }) => (
    <div className="info-card">
      <div className="icon-box"><Icon size={20} /></div>
      <div className="info-content">
        <label>{label}</label>
        <p>{value || "Not specified"}</p>
      </div>
    </div>
  );

  return (
    <div className="profile-page-wrapper">
      <Navbar />
      
      <div className="profile-container">
        <div className="profile-main-card">
          
          {/* 1. HERO BANNER */}
          <div className="profile-banner">
            {!isEditing ? (
              <button className="edit-action-btn" onClick={() => setIsEditing(true)}>
                <Edit3 size={18} /> Edit Profile
              </button>
            ) : (
              <button className="cancel-action-btn" onClick={() => setIsEditing(false)}>
                <X size={18} /> Cancel
              </button>
            )}
          </div>

          {/* 2. LOGO & NAME SECTION */}
          <div className="profile-header-content">
            <div className="logo-section">
              <div className="logo-holder">
                {logoPreview ? <img src={logoPreview} alt="Logo" /> : <Building2 size={50} color="#cbd5e1" />}
                {isEditing && (
                  <label className="logo-upload-overlay">
                    <Camera size={24} />
                    <input type="file" hidden onChange={(e) => {
                      const file = e.target.files[0];
                      if(file) { setLogoFile(file); setLogoPreview(URL.createObjectURL(file)); }
                    }} />
                  </label>
                )}
              </div>
              <div className="name-box">
                <h1>{formData.companyName || "Company Name"}</h1>
               
              </div>
            </div>
          </div>

          {/* 3. CONTENT AREA (SWITCHES BETWEEN VIEW AND EDIT) */}
          <div className="profile-body">
            {!isEditing ? (
              /* --- VIEW MODE SCREEN --- */
              <div className="view-grid">
                <div className="grid-full"><h3 className="section-title">Contact Information</h3></div>
                <InfoCard icon={Mail} label="Official Email" value={formData.officialEmail} />
                <InfoCard icon={Phone} label="Phone Number" value={formData.phoneNumber} />
                
                <div className="grid-full" style={{marginTop: '20px'}}><h3 className="section-title">Locations</h3></div>
                <InfoCard icon={MapPin} label="Headquarters" value={formData.cityAndCountry} />
                <InfoCard icon={Globe} label="Additional Offices" value={formData.otherOfficeLocations} />
              </div>
            ) : (
              /* --- EDIT MODE FORM --- */
              <form onSubmit={handleSave} className="edit-form">
                <h3 className="section-title">Edit Company Details</h3>
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Company Name</label>
                    <input value={formData.companyName} onChange={e => setFormData({...formData, companyName: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Official Email</label>
                    <input type="email" value={formData.officialEmail} onChange={e => setFormData({...formData, officialEmail: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input value={formData.phoneNumber} onChange={e => setFormData({...formData, phoneNumber: e.target.value})} required />
                  </div>
                  <div className="form-group full">
                    <label>City & Country</label>
                    <input value={formData.cityAndCountry} onChange={e => setFormData({...formData, cityAndCountry: e.target.value})} required />
                  </div>
                  <div className="form-group full">
                    <label>Other Office Locations</label>
                    <textarea value={formData.otherOfficeLocations} onChange={e => setFormData({...formData, otherOfficeLocations: e.target.value})} />
                  </div>
                </div>
                <div className="form-footer">
                  <button type="submit" disabled={loading} className="save-btn">
                    {loading ? <Loader2 className="animate-spin" /> : <Save size={18} />}
                    {loading ? "Saving..." : "Save Profile Changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .profile-page-wrapper { background: #f4f7fe; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .profile-container { max-width: 1000px; margin: 0 auto; padding: 40px 20px; }
        
        .profile-main-card { background: #fff; border-radius: 30px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.05); }
        
        .profile-banner { height: 180px; background: linear-gradient(135deg, #9995f0 0%, #9764f0 100%); padding: 30px; display: flex; justify-content: flex-end; align-items: flex-start; }
        
        .edit-action-btn, .cancel-action-btn { padding: 10px 20px; border-radius: 12px; border: none; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 14px; }
        .edit-action-btn { background: rgba(255,255,255,0.2); color: #fff; backdrop-filter: blur(10px); }
        .cancel-action-btn { background: #fee2e2; color: #ef4444; }

        .profile-header-content { padding: 0 50px; margin-top: -70px; margin-bottom: 40px; }
        .logo-section { display: flex; align-items: flex-end; gap: 25px; }
        
        .logo-holder { width: 140px; height: 140px; background: #fff; border-radius: 25px; padding: 10px; border: 5px solid #fff; box-shadow: 0 10px 20px rgba(0,0,0,0.1); position: relative; display: flex; justify-content: center; align-items: center; overflow: hidden; }
        .logo-holder img { width: 100%; height: 100%; object-fit: contain; }
        
        .logo-upload-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.4); color: #fff; display: flex; justify-content: center; align-items: center; cursor: pointer; opacity: 1; transition: 0.3s; }

        .name-box h1 { margin: 0 0 5px 0; font-size: 30px; font-weight: 800; color: #1e293b; }
        .status-badge { display: flex; align-items: center; gap: 5px; color: #10b981; font-weight: 700; font-size: 13px; }

        .profile-body { padding: 0 50px 50px 50px; }
        .section-title { font-size: 14px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 20px; font-weight: 800; }

        .view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .grid-full { grid-column: 1 / -1; }

        .info-card { background: #f8fafc; padding: 20px; border-radius: 20px; display: flex; align-items: center; gap: 15px; border: 1px solid #f1f5f9; }
        .icon-box { width: 45px; height: 45px; background: #fff; border-radius: 12px; display: flex; justify-content: center; align-items: center; color: #4f46e5; box-shadow: 0 4px 10px rgba(0,0,0,0.03); }
        .info-content label { display: block; font-size: 12px; color: #64748b; font-weight: 600; margin-bottom: 2px; }
        .info-content p { margin: 0; font-weight: 700; color: #1e293b; font-size: 15px; }

        .edit-form { animation: fadeIn 0.3s ease-in-out; }
        .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .form-group.full { grid-column: 1 / -1; }
        .form-group label { display: block; font-size: 13px; font-weight: 700; color: #475569; margin-bottom: 8px; }
        .form-group input, .form-group textarea { width: 100%; padding: 14px; border-radius: 12px; border: 2px solid #f1f5f9; background: #f8fafc; outline: none; font-weight: 500; font-size: 15px; transition: 0.3s; }
        .form-group input:focus { border-color: #4f46e5; background: #fff; }
        
        .form-footer { margin-top: 30px; display: flex; justify-content: flex-end; }
        .save-btn { background: #4f46e5; color: #fff; border: none; padding: 15px 35px; border-radius: 15px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 10px; font-size: 15px; box-shadow: 0 10px 20px rgba(79, 70, 229, 0.2); }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default CompanyProfile;